import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Login,
  AddCircleOutline,
  EditOutlined,
  DeleteOutline,
  History,
  CheckCircle,
  ErrorOutline,
  InfoOutlined,
  Timeline,
} from '@mui/icons-material';

interface Activity {
  id: string;
  action: string;
  user: string;
  county?: string;
  created_at: string;
  type?: 'login' | 'create' | 'update' | 'delete' | 'success' | 'error' | 'info';
}

interface ActivityFeedProps {
  activities: Activity[];
  isLoading?: boolean;
  maxItems?: number;
}

const getActivityIcon = (action: string, type?: string) => {
  const actionLower = action.toLowerCase();

  if (type === 'success' || actionLower.includes('success') || actionLower.includes('completed')) {
    return <CheckCircle sx={{ fontSize: 20 }} />;
  }
  if (type === 'error' || actionLower.includes('failed') || actionLower.includes('error')) {
    return <ErrorOutline sx={{ fontSize: 20 }} />;
  }
  if (type === 'login' || actionLower.includes('login')) {
    return <Login sx={{ fontSize: 20 }} />;
  }
  if (type === 'create' || actionLower.includes('create') || actionLower.includes('add')) {
    return <AddCircleOutline sx={{ fontSize: 20 }} />;
  }
  if (type === 'update' || actionLower.includes('update') || actionLower.includes('edit')) {
    return <EditOutlined sx={{ fontSize: 20 }} />;
  }
  if (type === 'delete' || actionLower.includes('delete') || actionLower.includes('remove')) {
    return <DeleteOutline sx={{ fontSize: 20 }} />;
  }
  return <History sx={{ fontSize: 20 }} />;
};

const getActivityColor = (action: string, type?: string): { bg: string; text: string } => {
  const actionLower = action.toLowerCase();

  if (type === 'success' || actionLower.includes('success') || actionLower.includes('completed')) {
    return { bg: 'rgba(46, 196, 182, 0.12)', text: 'success.main' };
  }
  if (type === 'error' || actionLower.includes('failed') || actionLower.includes('error')) {
    return { bg: 'rgba(231, 29, 54, 0.12)', text: 'error.main' };
  }
  if (type === 'login' || actionLower.includes('login')) {
    return { bg: 'rgba(108, 99, 255, 0.12)', text: 'primary.main' };
  }
  if (type === 'create' || actionLower.includes('create') || actionLower.includes('add')) {
    return { bg: 'rgba(46, 196, 182, 0.12)', text: 'success.main' };
  }
  if (type === 'update' || actionLower.includes('update') || actionLower.includes('edit')) {
    return { bg: 'rgba(13, 202, 240, 0.12)', text: 'info.main' };
  }
  if (type === 'delete' || actionLower.includes('delete') || actionLower.includes('remove')) {
    return { bg: 'rgba(231, 29, 54, 0.12)', text: 'error.main' };
  }
  return { bg: 'rgba(108, 117, 125, 0.12)', text: 'text.secondary' };
};

export const ActivityFeed = ({ activities, isLoading = false, maxItems = 5 }: ActivityFeedProps) => {
  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          height: '100%',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          boxShadow: '0 2px 8px rgba(108, 99, 255, 0.08)',
        }}
      >
        <CardHeader
          title={<Skeleton width={150} />}
          subheader={<Skeleton width={100} />}
        />
        <CardContent sx={{ pt: 2 }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 3 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" />
                <Skeleton width="80%" />
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  const displayActivities = activities.slice(0, maxItems);

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        boxShadow: '0 2px 8px rgba(108, 99, 255, 0.08)',
        transition: 'all 0.3s ease',
        animation: 'slideInUp 0.7s ease-out',
        '@keyframes slideInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '&:hover': {
          boxShadow: '0 8px 24px rgba(108, 99, 255, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Timeline sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#1f2937' }}>
                Recent Activity
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                System pulse
              </Typography>
            </Box>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 2 }}>
        {displayActivities.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <InfoOutlined sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">No recent activity</Typography>
          </Box>
        ) : (
          <Box>
            {displayActivities.map((activity, index) => {
              const colors = getActivityColor(activity.action, activity.type);

              return (
                <Box
                  key={activity.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    mb: index < displayActivities.length - 1 ? 3 : 0,
                    p: 1.5,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    animation: `fadeInActivity ${0.3 + index * 0.1}s ease-out`,
                    '@keyframes fadeInActivity': {
                      from: {
                        opacity: 0,
                        transform: 'translateX(-10px)',
                      },
                      to: {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(108, 99, 255, 0.04)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: colors.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: colors.text,
                      mr: 2,
                      flexShrink: 0,
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {getActivityIcon(activity.action, activity.type)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: '0.9375rem',
                        }}
                      >
                        {activity.action}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          ml: 1,
                        }}
                      >
                        {activity.created_at}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {activity.user}
                      </Box>
                      {activity.county && (
                        <>
                          <Box component="span" sx={{ mx: 1 }}>•</Box>
                          {activity.county}
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
